import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  BulkRolePermissionAssignmentRequest,
  PermissionDTO,
  PermissionPolicyOperationRequest,
  ResourceDTO,
  ScopeDTO,
  SeederJobDTO,
} from '../types/permission.types';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/auth/api/admin/permissions`;

  // ── Permissions ────────────────────────────────────────────────────────
  list(): Observable<PermissionDTO[]> {
    return this.http.get<PermissionDTO[]>(this.base);
  }
  get(id: string): Observable<PermissionDTO> {
    return this.http.get<PermissionDTO>(`${this.base}/${encodeURIComponent(id)}`);
  }
  create(req: PermissionDTO): Observable<PermissionDTO> {
    return this.http.post<PermissionDTO>(this.base, req);
  }
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${encodeURIComponent(id)}`);
  }
  managePolicies(
    id: string,
    req: PermissionPolicyOperationRequest,
  ): Observable<void> {
    return this.http.post<void>(
      `${this.base}/${encodeURIComponent(id)}/policies`,
      req,
    );
  }
  bulkRoleAssignment(
    req: BulkRolePermissionAssignmentRequest,
  ): Observable<void> {
    return this.http.post<void>(`${this.base}/bulk/role-assignment`, req);
  }

  // ── Resources ──────────────────────────────────────────────────────────
  listResources(): Observable<ResourceDTO[]> {
    return this.http.get<ResourceDTO[]>(`${this.base}/resources`);
  }
  getResource(id: string): Observable<ResourceDTO> {
    return this.http.get<ResourceDTO>(`${this.base}/resources/${encodeURIComponent(id)}`);
  }
  createResource(req: ResourceDTO): Observable<ResourceDTO> {
    return this.http.post<ResourceDTO>(`${this.base}/resources`, req);
  }
  updateResource(id: string, req: ResourceDTO): Observable<ResourceDTO> {
    return this.http.put<ResourceDTO>(`${this.base}/resources/${encodeURIComponent(id)}`, req);
  }
  deleteResource(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/resources/${encodeURIComponent(id)}`);
  }

  // ── Scopes ─────────────────────────────────────────────────────────────
  listScopes(): Observable<ScopeDTO[]> {
    return this.http.get<ScopeDTO[]>(`${this.base}/scopes`);
  }
  getScope(id: string): Observable<ScopeDTO> {
    return this.http.get<ScopeDTO>(`${this.base}/scopes/${encodeURIComponent(id)}`);
  }
  createScope(req: ScopeDTO): Observable<ScopeDTO> {
    return this.http.post<ScopeDTO>(`${this.base}/scopes`, req);
  }
  deleteScope(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/scopes/${encodeURIComponent(id)}`);
  }

  // ── Seeder (async — idempotent setup) ──────────────────────────────────
  /**
   * Lance le seeder en arrière-plan.
   *
   * - 202 Accepted : retourne un `SeederJobDTO` avec status `PENDING` à poller.
   * - 409 Conflict : un job tourne déjà — le body 409 contient le job en
   *   cours (`SeederJobDTO`), à pratiquer depuis l'intercepteur d'erreur du
   *   composant pour reprendre le polling sans relancer.
   */
  startSeed(): Observable<SeederJobDTO> {
    return this.http.post<SeederJobDTO>(`${this.base}/seed`, {});
  }

  /** Récupère le snapshot d'un job (status, progress, result). À poller. */
  getSeedJob(jobId: string): Observable<SeederJobDTO> {
    return this.http.get<SeederJobDTO>(
      `${this.base}/seed/jobs/${encodeURIComponent(jobId)}`,
    );
  }

  /** Historique des 20 derniers jobs (récent → ancien). */
  listSeedJobs(): Observable<SeederJobDTO[]> {
    return this.http.get<SeederJobDTO[]>(`${this.base}/seed/jobs`);
  }
}
